/**
 * AddonsSettings - Zusatzleistungen / Upselling Einstellungen
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Gift, Plus, Trash2, Save, Loader2, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AddonsSettings = () => {
  const [addonSettings, setAddonSettings] = useState({
    enabled: false,
    assistant_trainer_id: null,
    mappings: [],
    warmup_times: {},
    // NEW: Multi-Addon lists [{service_id, combo_price}]
    default_addons: [],
    probe_addons: [],
    excluded_main_service_ids: [],
    probe_main_service_ids: [],
    loyalty_combo_enabled: false,
    loyalty_min_trainings_per_month: 2,
  });
  const [addonServices, setAddonServices] = useState([]);
  const [addonTrainers, setAddonTrainers] = useState([]);
  const [studios, setStudios] = useState([]); // Iter 291: Studios für Multi-Studio-Badges
  const [loadingAddons, setLoadingAddons] = useState(true);
  const [savingAddons, setSavingAddons] = useState(false);

  // Konflikt-Warner
  const [conflictDialog, setConflictDialog] = useState({
    open: false,
    conflicts: [], // [{ index, mainServiceName, addonName, reason, mappingId }]
  });

  useEffect(() => {
    loadAddonSettings();
  }, []);

  const loadAddonSettings = async () => {
    setLoadingAddons(true);
    try {
      const token = localStorage.getItem('token');
      const requests = await Promise.allSettled([
        axios.get(`${API}/settings/addon-services`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/services`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users?role=trainer`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/studios`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const [settingsRes, servicesRes, trainersRes, studiosRes] = requests;

      const settingsData = settingsRes.status === 'fulfilled' ? settingsRes.value.data : {};
      const servicesData = servicesRes.status === 'fulfilled' ? servicesRes.value.data : [];
      const trainersData = trainersRes.status === 'fulfilled' ? trainersRes.value.data : [];
      const studiosData = studiosRes.status === 'fulfilled' ? studiosRes.value.data : [];

      const sourceMappings = Array.isArray(settingsData.mappings) ? settingsData.mappings : [];
      const seenMappingIds = new Set();
      const normalizedMappings = sourceMappings.map((mapping, idx) => {
        const baseId = String(
          mapping?.id ||
          `mapping-${mapping?.main_service_id || 'main'}-${mapping?.addon_service_id || 'addon'}-${idx}`
        );
        let uniqueId = baseId;
        let counter = 1;
        while (seenMappingIds.has(uniqueId)) {
          uniqueId = `${baseId}-${counter++}`;
        }
        seenMappingIds.add(uniqueId);
        return { ...mapping, id: uniqueId };
      });
      
      setAddonSettings({
        enabled: settingsData.enabled ?? false,
        assistant_trainer_id: settingsData.assistant_trainer_id || null,
        mappings: normalizedMappings,
        warmup_times: settingsData.warmup_times || {},
        default_addons: Array.isArray(settingsData.default_addons)
          ? settingsData.default_addons.map(a => ({
              service_id: String(a.service_id || ''),
              combo_price: a.combo_price ?? 0,
            })).filter(a => a.service_id)
          : [],
        probe_addons: Array.isArray(settingsData.probe_addons)
          ? settingsData.probe_addons.map(a => ({
              service_id: String(a.service_id || ''),
              combo_price: a.combo_price ?? 0,
            })).filter(a => a.service_id)
          : [],
        excluded_main_service_ids: Array.isArray(settingsData.excluded_main_service_ids) ? settingsData.excluded_main_service_ids : [],
        probe_main_service_ids: Array.isArray(settingsData.probe_main_service_ids) ? settingsData.probe_main_service_ids : [],
        loyalty_combo_enabled: settingsData.loyalty_combo_enabled ?? false,
        loyalty_min_trainings_per_month: settingsData.loyalty_min_trainings_per_month ?? 2,
      });

      setAddonServices(Array.isArray(servicesData) ? servicesData : []);
      setStudios(Array.isArray(studiosData) ? studiosData : []);
      setAddonTrainers(
        Array.isArray(trainersData)
          ? trainersData.map((trainer, idx) => ({
              id: trainer.id || trainer.user_id || trainer.email || `trainer-${idx}`,
              first_name: trainer.first_name || '',
              last_name: trainer.last_name || '',
            }))
          : []
      );

      const failedParts = [];
      if (settingsRes.status !== 'fulfilled') failedParts.push('Einstellungen');
      if (servicesRes.status !== 'fulfilled') failedParts.push('Services');
      if (trainersRes.status !== 'fulfilled') failedParts.push('Trainer');
      if (failedParts.length > 0) {
        toast.warning(`Teilweise geladen: ${failedParts.join(', ')}`);
      }
    } catch (error) {
      console.error('Error loading addon settings:', error);
      toast.error('Fehler beim Laden der Add-on Einstellungen');
    } finally {
      setLoadingAddons(false);
    }
  };

  /**
   * Findet Service-Zuordnungen die redundant zu Standard- oder Probe-Addons sind.
   * Zuordnung ist redundant wenn:
   * - Same addon_service_id wie ein Standard-Addon UND
   * - main_service nicht in excluded_main_service_ids UND
   * - combo_price identisch zum Standard
   * Oder analog für Probe-Addons (main_service_id in probe_main_service_ids + gleicher Preis).
   */
  const detectConflicts = () => {
    const conflicts = [];
    const { mappings = [], default_addons = [], probe_addons = [],
            excluded_main_service_ids = [], probe_main_service_ids = [] } = addonSettings;

    const serviceName = (id) => {
      const s = addonServices.find(x => String(x.id) === String(id));
      return s?.name || 'Unbekannt';
    };

    (mappings || []).forEach((m, idx) => {
      const mainId = String(m.main_service_id || '');
      const addonId = String(m.addon_service_id || '');
      const price = Number(m.combo_price ?? 0);
      if (!mainId || !addonId) return;

      // Check against default_addons
      const matchingDefault = default_addons.find(d =>
        String(d.service_id) === addonId &&
        Number(d.combo_price ?? 0) === price
      );
      if (matchingDefault && !excluded_main_service_ids.includes(mainId)) {
        conflicts.push({
          mappingIndex: idx,
          mainServiceName: serviceName(mainId),
          addonName: serviceName(addonId),
          price,
          reason: 'Standard-Add-on',
          mappingId: m.id || `idx-${idx}`,
        });
        return;
      }

      // Check against probe_addons
      if (probe_main_service_ids.includes(mainId)) {
        const matchingProbe = probe_addons.find(p =>
          String(p.service_id) === addonId &&
          Number(p.combo_price ?? 0) === price
        );
        if (matchingProbe) {
          conflicts.push({
            mappingIndex: idx,
            mainServiceName: serviceName(mainId),
            addonName: serviceName(addonId),
            price,
            reason: 'Probe-Add-on',
            mappingId: m.id || `idx-${idx}`,
          });
        }
      }
    });

    return conflicts;
  };

  const saveAddonSettings = async () => {
    // Detect conflicts before saving
    const conflicts = detectConflicts();
    if (conflicts.length > 0) {
      setConflictDialog({ open: true, conflicts });
      return;
    }
    await persistAddonSettings();
  };

  const persistAddonSettings = async (settingsOverride = null) => {
    setSavingAddons(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/settings/addon-services`,
        settingsOverride || addonSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Add-on Einstellungen gespeichert');
    } catch (error) {
      console.error('Error saving addon settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingAddons(false);
    }
  };

  const addAddonMapping = () => {
    setAddonSettings(prev => ({
      ...prev,
      mappings: [
        ...prev.mappings,
        {
          id: `new-${Date.now()}`,
          main_service_id: '',
          addon_service_id: '',
          combo_price: 0,
          is_active: true
        }
      ]
    }));
  };

  const updateAddonMapping = (mappingId, field, value) => {
    setAddonSettings(prev => ({
      ...prev,
      mappings: prev.mappings.map(m => 
        m.id === mappingId ? { ...m, [field]: value } : m
      )
    }));
  };

  const removeAddonMapping = (mappingId) => {
    setAddonSettings(prev => ({
      ...prev,
      mappings: prev.mappings.filter(m => m.id !== mappingId)
    }));
  };

  const updateWarmupTime = (serviceId, value) => {
    setAddonSettings(prev => ({
      ...prev,
      warmup_times: {
        ...prev.warmup_times,
        [serviceId]: parseInt(value) || 0
      }
    }));
  };

  // Iter 351i: ON/OFF-Schalter — ON setzt default 10 Min., OFF setzt 0.
  const toggleWarmup = (serviceId, enabled) => {
    setAddonSettings(prev => ({
      ...prev,
      warmup_times: {
        ...prev.warmup_times,
        [serviceId]: enabled ? (parseInt(prev.warmup_times[serviceId]) || 10) : 0
      }
    }));
  };

  if (loadingAddons) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  // Iter 291: Multi-Studio Helper
  // Liefert für einen Service kompakte Studio-Labels („📍 Wetzikon" oder
  // „📍 Wetzikon · Rapperswil"). Wenn keine Studios konfiguriert sind →
  // „📍 Alle Studios" (= Legacy / global).
  const getServiceStudios = (service) => {
    if (!service) return [];
    const sids = service.studio_ids && service.studio_ids.length > 0
      ? service.studio_ids
      : (service.studio_id ? [service.studio_id] : []);
    if (sids.length === 0) return [];
    return sids
      .map(sid => studios.find(s => s.id === sid))
      .filter(Boolean);
  };

  // Kleines Badge unter/neben einem Service-Eintrag
  const StudioBadge = ({ service, compact = false }) => {
    const matched = getServiceStudios(service);
    const allStudios = matched.length === 0;
    const isMultiStudio = studios.length > 1;
    // Wenn nur ein Studio existiert oder Service in allen Studios verfügbar ist
    // → kein Badge nötig (würde nur Lärm sein).
    if (!isMultiStudio) return null;
    if (allStudios) {
      return (
        <span
          className={`inline-flex items-center gap-1 ${compact ? 'text-[10px]' : 'text-xs'} text-gray-500 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5`}
          data-testid="addon-studio-badge-all"
        >
          📍 Alle Studios
        </span>
      );
    }
    const isExclusive = matched.length === 1;
    return (
      <span
        className={`inline-flex items-center gap-1 ${compact ? 'text-[10px]' : 'text-xs'} font-medium rounded px-1.5 py-0.5 border ${
          isExclusive
            ? 'text-amber-700 bg-amber-50 border-amber-300'
            : 'text-emerald-700 bg-emerald-50 border-emerald-300'
        }`}
        title={isExclusive ? 'Nur in diesem Standort verfügbar' : 'In mehreren Standorten verfügbar'}
        data-testid={`addon-studio-badge-${matched.map(s => s.short_name || s.name).join('-')}`}
      >
        📍 {matched.map(s => s.short_name || s.name).join(' · ')}
        {isExclusive && <span className="ml-0.5 opacity-70">(nur hier)</span>}
      </span>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-amber-600" />
          Zusatzleistungen
        </CardTitle>
        <CardDescription>
          Bieten Sie Kunden nach einer Buchung zusätzliche Services an (Upselling)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Feature aktivieren */}
        <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div>
            <h4 className="font-semibold">Zusatzleistungen aktivieren</h4>
            <p className="text-sm text-gray-600">
              Nach einer Buchung wird dem Kunden ein passendes Add-on angeboten
            </p>
          </div>
          <Switch
            checked={addonSettings.enabled}
            onCheckedChange={(checked) => setAddonSettings(prev => ({ ...prev, enabled: checked }))}
          />
        </div>

        {addonSettings.enabled && (
          <>
            {/* Iter 339: Hinweis-Banner zur strikten Mapping-Logik. */}
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4" data-testid="strict-mapping-info-banner">
              <h4 className="font-semibold text-amber-900 flex items-center gap-2">
                ✅ Strikte Zuordnung aktiv (Iter 339)
              </h4>
              <p className="text-sm text-amber-900 mt-1">
                Add-ons werden seit Update 339 ausschließlich angezeigt, wenn sie explizit
                im Bereich &bdquo;🎁 Service-Zuordnungen&ldquo; einem Hauptservice zugeordnet sind.
                <br/>
                <span className="text-xs text-amber-800">
                  Die unten stehende Probe-Training-Liste sowie alte Standard-Add-on-Felder
                  haben <strong>keinen Effekt mehr</strong> auf das, was dem Kunden bei der Buchung
                  angeboten wird. Bitte alle gewünschten Kombinationen direkt unter
                  &bdquo;Service-Zuordnungen&ldquo; einrichten.
                </span>
              </p>
            </div>

            {/* Ersatz-Trainer */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                👥 Ersatz-Trainer für Add-ons
              </Label>
              <Select
                value={addonSettings.assistant_trainer_id || 'none'}
                onValueChange={(value) => setAddonSettings(prev => ({ 
                  ...prev, 
                  assistant_trainer_id: value === 'none' ? null : value 
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Trainer auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Ersatz-Trainer</SelectItem>
                  {addonTrainers.map((trainer, idx) => (
                    <SelectItem key={`${trainer.id}-${idx}`} value={trainer.id}>
                      {trainer.first_name} {trainer.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Wenn der ursprüngliche Trainer nicht verfügbar ist, wird dieser Trainer für Add-ons verwendet
              </p>
            </div>

            {/* Standard-Add-ons Block entfernt — User-Wunsch (07.05.2026):
                Die Funktion „🎁 Service-Zuordnungen" deckt diesen Use-Case
                vollständig ab und ist verlässlich. Standard-Add-ons-UI war
                fehleranfällig und wurde deshalb aus dem Admin-Portal entfernt. */}

            {/* Probe-Training Spezial-Addons — MULTI-LIST
                Iter 339: Diese Liste wird NICHT mehr automatisch als Fallback angewendet.
                Sie bleibt aus Kompatibilitätsgründen sichtbar, ist aber funktional inaktiv.
            */}
            <div className="space-y-3 border-2 border-gray-300 bg-gray-50 rounded-lg p-4 opacity-70">
              <div>
                <Label className="flex items-center gap-2 text-base font-semibold text-gray-600">
                  🎯 Probe-Training Spezial-Add-ons <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-700">Veraltet</span>
                </Label>
                <p className="text-xs text-gray-600 mt-1">
                  ⚠️ Seit Iter 339 nicht mehr aktiv — bitte stattdessen unter &bdquo;🎁 Service-Zuordnungen&ldquo; eintragen.
                </p>
              </div>

              {(addonSettings.probe_addons || []).map((item, idx) => (
                <div key={`probe-addon-${idx}`} className="grid grid-cols-1 md:grid-cols-[1fr_140px_40px] gap-3 items-end bg-white rounded-md p-3 border">
                  <div>
                    <Label className="text-xs">Probe-Add-on (z.B. Kryosauna Probe)</Label>
                    <Select
                      value={item.service_id || 'none'}
                      onValueChange={(v) => setAddonSettings(prev => ({
                        ...prev,
                        probe_addons: prev.probe_addons.map((a, i) => i === idx ? { ...a, service_id: v === 'none' ? '' : v } : a),
                      }))}
                    >
                      <SelectTrigger data-testid={`probe-addon-service-${idx}`}><SelectValue placeholder="Probe-Service wählen" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- wählen --</SelectItem>
                        {addonServices.map((s, i) => (
                          <SelectItem key={`probe-${idx}-${s.id}-${i}`} value={String(s.id)}>
                            <span className="flex items-center gap-2">
                              <span>{s.name}</span>
                              <StudioBadge service={s} compact />
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Probe-Kombi-Preis (CHF)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      placeholder="z.B. 15"
                      value={item.combo_price ?? ''}
                      onChange={(e) => setAddonSettings(prev => ({
                        ...prev,
                        probe_addons: prev.probe_addons.map((a, i) => i === idx ? { ...a, combo_price: e.target.value === '' ? 0 : parseFloat(e.target.value) } : a),
                      }))}
                      data-testid={`probe-addon-price-${idx}`}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setAddonSettings(prev => ({
                      ...prev,
                      probe_addons: prev.probe_addons.filter((_, i) => i !== idx),
                    }))}
                    data-testid={`probe-addon-remove-${idx}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))}

              <Button
                onClick={() => setAddonSettings(prev => ({
                  ...prev,
                  probe_addons: [...(prev.probe_addons || []), { service_id: '', combo_price: 0 }],
                }))}
                size="sm"
                variant="outline"
                className="gap-2"
                data-testid="probe-addon-add-btn"
              >
                <Plus size={16} />
                Probe-Add-on hinzufügen
              </Button>

              {(addonSettings.probe_addons || []).length > 0 && (
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    ✅ Welche Services gelten als Probe-Training?
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto p-2 bg-white rounded border">
                    {(() => {
                      const seen = new Set();
                      return addonServices.filter(s => {
                        const k = String(s.id); if (seen.has(k)) return false; seen.add(k); return true;
                      }).map((s, idx) => {
                        const sid = String(s.id);
                        const selected = (addonSettings.probe_main_service_ids || []).includes(sid);
                        return (
                          <label key={`probe-main-${s.id}-${idx}`} className={`flex items-center gap-2 text-xs p-2 rounded cursor-pointer ${selected ? 'bg-blue-100' : 'hover:bg-gray-50'}`}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setAddonSettings(prev => {
                                  const curr = prev.probe_main_service_ids || [];
                                  return {
                                    ...prev,
                                    probe_main_service_ids: checked
                                      ? [...curr.filter(x => x !== sid), sid]
                                      : curr.filter(x => x !== sid),
                                  };
                                });
                              }}
                              className="accent-blue-600"
                            />
                            <span>{s.name}</span>
                          </label>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Add-on Mappings */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="flex items-center gap-2 text-lg">
                    🎁 Service-Zuordnungen
                  </Label>
                  <p className="text-sm text-gray-500 mt-1">
                    Definieren Sie, welches Add-on nach welchem Service angeboten wird
                  </p>
                </div>
                <Button onClick={addAddonMapping} size="sm" className="gap-2">
                  <Plus size={16} />
                  Zuordnung hinzufügen
                </Button>
              </div>

              {addonSettings.mappings.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
                  <Gift className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">Noch keine Zuordnungen konfiguriert</p>
                  <Button onClick={addAddonMapping} variant="outline" className="mt-3 gap-2">
                    <Plus size={16} />
                    Erste Zuordnung hinzufügen
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {addonSettings.mappings.map((mapping, index) => {
                    const mainService = addonServices.find(s => String(s.id) === String(mapping.main_service_id));
                    const addonService = addonServices.find(s => String(s.id) === String(mapping.addon_service_id));
                    // Schnittmenge der Studios: nur dort wo BEIDE verfügbar sind
                    // wird das Mapping tatsächlich aktiv. Wenn z.B. EMS in beiden,
                    // Kryosauna nur in Wetzikon → Mapping greift nur in Wetzikon.
                    let intersectionStudios = null;
                    if (mainService && addonService && studios.length > 1) {
                      const mainSids = getServiceStudios(mainService).map(s => s.id);
                      const addonSids = getServiceStudios(addonService).map(s => s.id);
                      // Wenn ein Service in „Alle Studios" (leere Liste) ist,
                      // gilt die Beschränkung des anderen.
                      const effectiveMain = mainSids.length ? mainSids : studios.map(s => s.id);
                      const effectiveAddon = addonSids.length ? addonSids : studios.map(s => s.id);
                      intersectionStudios = effectiveMain.filter(id => effectiveAddon.includes(id));
                    }
                    return (
                    <div 
                      key={`${mapping.id || 'mapping'}-${index}`}
                      className="p-4 border rounded-lg bg-white shadow-sm space-y-4"
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="bg-amber-50">
                            Zuordnung #{index + 1}
                          </Badge>
                          {/* Iter 291: Studio-Hinweis pro Mapping */}
                          {intersectionStudios && mainService && addonService && (
                            <>
                              {intersectionStudios.length === 0 ? (
                                <span
                                  className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-300 rounded px-2 py-0.5"
                                  data-testid={`mapping-studio-warning-${index}`}
                                  title="Haupt- und Add-on-Service haben kein gemeinsames Studio. Diese Zuordnung wird nie greifen."
                                >
                                  ⚠️ Kein gemeinsames Studio
                                </span>
                              ) : intersectionStudios.length < studios.length ? (
                                <span
                                  className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-300 rounded px-2 py-0.5"
                                  data-testid={`mapping-studio-info-${index}`}
                                  title="Diese Zuordnung greift nur in diesen Standorten"
                                >
                                  📍 Nur in: {intersectionStudios.map(sid => {
                                    const st = studios.find(x => x.id === sid);
                                    return st?.short_name || st?.name || '?';
                                  }).join(' · ')}
                                </span>
                              ) : (
                                <span
                                  className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-300 rounded px-2 py-0.5"
                                  data-testid={`mapping-studio-all-${index}`}
                                  title="In allen Standorten verfügbar"
                                >
                                  📍 Alle Standorte
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={mapping.is_active}
                            onCheckedChange={(checked) => updateAddonMapping(mapping.id, 'is_active', checked)}
                          />
                          <span className="text-sm text-gray-500">Aktiv</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAddonMapping(mapping.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Haupt-Service */}
                        <div className="space-y-2">
                          <Label>Nach Buchung von:</Label>
                          <Select
                            value={mapping.main_service_id || ''}
                            onValueChange={(value) => updateAddonMapping(mapping.id, 'main_service_id', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Service wählen" />
                            </SelectTrigger>
                            <SelectContent>
                              {addonServices.map((service, serviceIndex) => {
                                const serviceValue = String(service.id || service.service_id || `service-${serviceIndex}`);
                                return (
                                  <SelectItem key={`${serviceValue}-main-${serviceIndex}`} value={serviceValue}>
                                    <span className="flex items-center gap-2">
                                      <span>{service.name}</span>
                                      <StudioBadge service={service} compact />
                                    </span>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Add-on Service */}
                        <div className="space-y-2">
                          <Label>Add-on anbieten:</Label>
                          <Select
                            value={mapping.addon_service_id || ''}
                            onValueChange={(value) => updateAddonMapping(mapping.id, 'addon_service_id', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Add-on wählen" />
                            </SelectTrigger>
                            <SelectContent>
                              {addonServices.map((service, serviceIndex) => {
                                const serviceValue = String(service.id || service.service_id || `service-${serviceIndex}`);
                                return (
                                  <SelectItem key={`${serviceValue}-addon-${serviceIndex}`} value={serviceValue}>
                                    <span className="flex items-center gap-2">
                                      <span>{service.name} ({service.price || 0} CHF)</span>
                                      <StudioBadge service={service} compact />
                                    </span>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Kombi-Preis */}
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <span className="font-bold text-sm">CHF</span>
                            Kombi-Preis
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={mapping.combo_price || ''}
                            onChange={(e) => updateAddonMapping(mapping.id, 'combo_price', parseFloat(e.target.value) || 0)}
                            placeholder="z.B. 25"
                          />
                          <p className="text-xs text-gray-500">
                            Reduzierter Preis für das Add-on
                          </p>
                        </div>
                      </div>
                    </div>
                  );})}
                </div>
              )}
            </div>

            {/* Aufwärm-Zeiten */}
            <div className="space-y-4 border-t pt-6">
              <div>
                <Label className="flex items-center gap-2 text-lg">
                  ⏱️ Aufwärm-Zeiten pro Service
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Zeit die der Kunde vor diesem Service zum Aufwärmen braucht. 
                  <strong> Das Add-on wird nur angeboten, wenn nach dem Add-on genug Zeit für das Aufwärmen des Folgetermins bleibt.</strong>
                </p>
                <p className="text-xs text-amber-600 mt-2">
                  💡 Beispiel: EMS Training braucht 10 Min. Warmup → Add-on wird nur angeboten wenn nach dem Add-on noch 10 Min. Puffer zum nächsten EMS sind.
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  🔁 <strong>ON</strong> = Ist der Trainer durch einen Folgetermin dieses Services belegt, darf der Ersatztrainer das Add-on übernehmen. <strong>OFF</strong> (z.B. Personal Training) = Add-on wird in dem Fall gar nicht angeboten.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(() => {
                  // Dedupliziere Services nach ID/Name — verhindert Duplikate wenn
                  // derselbe Service von mehreren Mitarbeitern angeboten wird.
                  const seen = new Set();
                  const uniqueServices = addonServices.filter((s) => {
                    const key = String(s.id || s.service_id || s.name);
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                  });
                  return uniqueServices.map((service, serviceIndex) => {
                    const serviceValue = String(service.id || service.service_id || `service-${serviceIndex}`);
                    const warmupOn = (parseInt(addonSettings.warmup_times[serviceValue]) || 0) > 0;
                    return (
                      <div key={`${serviceValue}-warmup-${serviceIndex}`} className={`flex items-center gap-3 p-3 rounded-lg ${warmupOn ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50'}`}>
                        <Switch
                          checked={warmupOn}
                          onCheckedChange={(checked) => toggleWarmup(serviceValue, checked)}
                          data-testid={`warmup-toggle-${serviceValue}`}
                        />
                        <span className="flex-1 text-sm font-medium">{service.name}</span>
                        {warmupOn ? (
                          <>
                            <Input
                              type="number"
                              min="1"
                              className="w-20"
                              value={addonSettings.warmup_times[serviceValue] || ''}
                              onChange={(e) => updateWarmupTime(serviceValue, e.target.value)}
                              placeholder="10"
                              data-testid={`warmup-minutes-${serviceValue}`}
                            />
                            <span className="text-sm text-gray-500">Min.</span>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 font-medium uppercase">Off</span>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </>
        )}

        {/* Treue-Kombi (08/2026) */}
        <div className="border rounded-lg p-4 space-y-3 bg-amber-50/50 border-amber-200" data-testid="loyalty-combo-section">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-sm">🏅 Treue-Kombi-Preis</p>
              <p className="text-xs text-gray-600">
                Kunden mit genügend Trainings im laufenden Monat zahlen für Zusatzdienste automatisch den Kombi-Preis –
                auch wenn sie den Zusatzdienst separat buchen (App, Portal & Admin).
              </p>
            </div>
            <Switch
              checked={addonSettings.loyalty_combo_enabled}
              onCheckedChange={(v) => setAddonSettings(prev => ({ ...prev, loyalty_combo_enabled: v }))}
              data-testid="loyalty-combo-toggle"
            />
          </div>
          {addonSettings.loyalty_combo_enabled && (
            <div className="flex items-center gap-3">
              <Label className="text-xs whitespace-nowrap">Mindestens Trainings pro Monat:</Label>
              <Input
                type="number"
                min="1"
                max="30"
                className="w-24"
                value={addonSettings.loyalty_min_trainings_per_month}
                onChange={(e) => setAddonSettings(prev => ({ ...prev, loyalty_min_trainings_per_month: parseInt(e.target.value) || 2 }))}
                data-testid="loyalty-combo-min-trainings"
              />
              <span className="text-xs text-gray-500">
                Der Kombi-Preis pro Zusatzdienst wird oben bei den Standard-Add-ons definiert (z.B. Kryosauna 35 statt 50).
              </span>
            </div>
          )}
        </div>

        {/* Speichern Button */}
        <div className="border-t pt-6">
          <Button 
            onClick={saveAddonSettings}
            disabled={savingAddons}
            className="w-full bg-amber-600 hover:bg-amber-700"
            data-testid="save-addon-settings-btn"
          >
            {savingAddons ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Wird gespeichert...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Einstellungen speichern
              </>
            )}
          </Button>
        </div>
      </CardContent>

      {/* ========== Konflikt-Warner Dialog ========== */}
      <Dialog
        open={conflictDialog.open}
        onOpenChange={(o) => setConflictDialog(prev => ({ ...prev, open: o }))}
      >
        <DialogContent className="max-w-lg" data-testid="addon-conflict-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Redundante Service-Zuordnungen gefunden
            </DialogTitle>
            <DialogDescription>
              {conflictDialog.conflicts.length === 1
                ? 'Eine Service-Zuordnung ist identisch zur Standard-/Probe-Add-on Regel und daher überflüssig:'
                : `${conflictDialog.conflicts.length} Service-Zuordnungen sind identisch zur Standard-/Probe-Add-on Regel und daher überflüssig:`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2 max-h-64 overflow-y-auto">
            {conflictDialog.conflicts.map((c, i) => (
              <div
                key={`conflict-${c.mappingId}-${i}`}
                className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200"
                data-testid={`conflict-item-${i}`}
              >
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm flex-1">
                  <div className="font-semibold">Zuordnung #{c.mappingIndex + 1}</div>
                  <div className="text-gray-700">
                    <span className="font-medium">{c.mainServiceName}</span> → {c.addonName} für <span className="font-medium">{c.price} CHF</span>
                  </div>
                  <div className="text-xs text-amber-700 mt-1">
                    Identisch zum {c.reason} — wird bereits automatisch angeboten.
                  </div>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button
              variant="outline"
              onClick={() => {
                setConflictDialog({ open: false, conflicts: [] });
                persistAddonSettings();
              }}
              data-testid="conflict-keep-btn"
            >
              Trotzdem speichern
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={async () => {
                const toRemove = new Set(conflictDialog.conflicts.map(c => c.mappingIndex));
                const cleanedSettings = {
                  ...addonSettings,
                  mappings: (addonSettings.mappings || []).filter((_, i) => !toRemove.has(i)),
                };
                setAddonSettings(cleanedSettings);
                setConflictDialog({ open: false, conflicts: [] });
                await persistAddonSettings(cleanedSettings);
                toast.success(
                  `${toRemove.size} redundante Zuordnung${toRemove.size === 1 ? '' : 'en'} entfernt`
                );
              }}
              data-testid="conflict-remove-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Redundante entfernen & speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AddonsSettings;
